import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import './LandingPage.css';
import './PenaltyShootout.css';
import { useSeo } from '../hooks/useSeo';
import { useJsonLd } from '../hooks/useJsonLd';
import FaqItem from './FaqItem';
import PublicPageNav from './PublicPageNav';


const REGULATION_ROUNDS = 5;

// SVG viewBox dimensions
const VB_W = 1000;
const VB_H = 680;

// Goal frame (inside viewBox)
const GOAL = { x: 170, y: 90, w: 660, h: 240 };
const GOAL_INNER_PAD = 22;
const PENALTY_SPOT = { x: VB_W / 2, y: 560 };

// Keeper positions (x,y is the keeper's feet anchor on the goal line)
const KEEPER_BASE_Y = GOAL.y + GOAL.h - 20;
const KEEPER_POS = {
  center: { x: GOAL.x + GOAL.w * 0.5, y: KEEPER_BASE_Y, angle: 0 },
  left: { x: GOAL.x + GOAL.w * 0.19, y: KEEPER_BASE_Y + 20, angle: -22 },
  right: { x: GOAL.x + GOAL.w * 0.81, y: KEEPER_BASE_Y + 20, angle: 22 },
};

const clampVal = (v, min, max) => Math.min(max, Math.max(min, v));

const getZoneFromX = (xSvg) => {
  const xNorm = (xSvg - GOAL.x) / GOAL.w;
  if (xNorm < 0.34) return 'left';
  if (xNorm < 0.66) return 'center';
  return 'right';
};

const computeSave = (ballZone, keeperZone, ballY) => {
  const yNorm = (ballY - GOAL.y) / GOAL.h;
  const topHalf = yNorm < 0.45;

  if (ballZone === keeperZone) {
    return Math.random() < (topHalf ? 0.45 : 0.8);
  }
  const zones = ['left', 'center', 'right'];
  const dist = Math.abs(zones.indexOf(ballZone) - zones.indexOf(keeperZone));
  if (dist === 1) {
    return Math.random() < (topHalf ? 0.04 : 0.14);
  }
  return Math.random() < 0.02;
};

// AI shot selector (weighted towards corners)
const pickAIShot = () => {
  const picks = [
    { zone: 'left', level: 'top', w: 2.2 },
    { zone: 'right', level: 'top', w: 2.2 },
    { zone: 'left', level: 'bottom', w: 3 },
    { zone: 'right', level: 'bottom', w: 3 },
    { zone: 'center', level: 'bottom', w: 1.2 },
    { zone: 'center', level: 'top', w: 0.8 },
  ];
  const total = picks.reduce((a, p) => a + p.w, 0);
  let r = Math.random() * total;
  let chosen = picks[0];
  for (const p of picks) {
    r -= p.w;
    if (r < 0) {
      chosen = p;
      break;
    }
  }
  const yRange = chosen.level === 'top'
    ? [GOAL.y + GOAL_INNER_PAD, GOAL.y + GOAL.h * 0.38]
    : [GOAL.y + GOAL.h * 0.55, GOAL.y + GOAL.h - GOAL_INNER_PAD];
  const xRanges = {
    left: [GOAL.x + GOAL_INNER_PAD + 6, GOAL.x + GOAL.w * 0.28],
    center: [GOAL.x + GOAL.w * 0.4, GOAL.x + GOAL.w * 0.6],
    right: [GOAL.x + GOAL.w * 0.72, GOAL.x + GOAL.w - GOAL_INNER_PAD - 6],
  };
  const xR = xRanges[chosen.zone];
  return {
    x: xR[0] + Math.random() * (xR[1] - xR[0]),
    y: yRange[0] + Math.random() * (yRange[1] - yRange[0]),
    zone: chosen.zone,
  };
};

// AI keeper dive (slightly biased to sides)
const pickAIDive = () => {
  const r = Math.random();
  if (r < 0.4) return 'left';
  if (r < 0.6) return 'center';
  return 'right';
};

const FAQ_ITEMS = [
  {
    question: 'Je li igra Penalty Shootout besplatna?',
    answer:
      'Da. Igra radi direktno u pregledniku, bez instalacije i bez registracije. Možeš je igrati odmah, koliko god puta želiš.',
  },
  {
    question: 'Kako se igra?',
    answer:
      'Naizmjenično pucaš i braniš protiv kompjutera. Dok pucaš, klikni negdje u gol da uputiš udarac - što bliže uglu to bolja šansa za gol. Dok braniš, odaberi stranu na koju ćeš skočiti (lijevo, sredina ili desno) prije nego AI uputi udarac.',
  },
  {
    question: 'Koliko traje jedan meč?',
    answer: `Klasično ${REGULATION_ROUNDS} udaraca i ${REGULATION_ROUNDS} obrana za svaku stranu. Ako je nakon regularnog dijela izjednačeno, ide se u sudden death - prvi koji promaši, izgubio je.`,
  },
  {
    question: 'Sprema li se statistika?',
    answer:
      'Ne. Penalty Shootout je brz javni alat za zabavu i ne sprema rezultate. Ako želiš pratiti pravu statistiku tvoje ekipe, koristi StatArena timski sustav.',
  },
  {
    question: 'Radi li igra na mobitelu?',
    answer:
      'Da. Igra je optimizirana za mobitel, tablet i desktop. Na mobitelu dodirni gol za udarac i dodirni gumbe ispod terena za obranu.',
  },
];

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const SOFTWARE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Penalty Shootout - Simulator jedanaesteraca',
  url: 'https://statarena.app/penalty-shootout',
  description:
    'Besplatna online igra jedanaesteraca. Pucaj i brani protiv kompjutera - klik za udarac, odaberi stranu za obranu. Radi u pregledniku bez instalacije.',
  applicationCategory: 'GameApplication',
  operatingSystem: 'Web',
  browserRequirements: 'Requires JavaScript. Requires HTML5.',
  inLanguage: 'hr',
  isAccessibleForFree: true,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'EUR',
  },
  genre: 'Sports',
  publisher: {
    '@type': 'Organization',
    name: 'StatArena',
    url: 'https://statarena.app',
    logo: 'https://statarena.app/icon-512.png',
  },
};

const BREADCRUMB_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Početna',
      item: 'https://statarena.app/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Penalty Shootout',
      item: 'https://statarena.app/penalty-shootout',
    },
  ],
};

const ScoreRow = ({ label, shots, total, active, highlight }) => {
  const slots = Math.max(total, shots.length);
  return (
    <div className={`penalty-scorerow ${active ? 'is-active' : ''}`}>
      <span className="penalty-scorerow-label">{label}</span>
      <div className="penalty-scorerow-dots">
        {Array.from({ length: slots }).map((_, i) => {
          const s = shots[i];
          return (
            <span
              key={i}
              className={`penalty-dot ${
                s === 'goal' ? 'is-goal' : s === 'save' ? 'is-save' : 'is-empty'
              }`}
              aria-label={s === 'goal' ? 'Gol' : s === 'save' ? 'Obranjeno' : 'Prazno'}
            >
              {s === 'goal' ? '●' : s === 'save' ? '✕' : '○'}
            </span>
          );
        })}
      </div>
      {highlight && <span className="penalty-scorerow-badge">{highlight}</span>}
    </div>
  );
};

const KeeperShape = () => (
  <g className="keeper-shape">
    {/* arms spread */}
    <rect x="-80" y="-50" width="160" height="22" rx="11" fill="#111827" />
    {/* gloves */}
    <rect x="-94" y="-56" width="30" height="32" rx="8" fill="#4ade80" stroke="#111" strokeWidth="1.5" />
    <rect x="64" y="-56" width="30" height="32" rx="8" fill="#4ade80" stroke="#111" strokeWidth="1.5" />
    {/* body */}
    <rect x="-26" y="-36" width="52" height="78" rx="14" fill="#facc15" stroke="#111" strokeWidth="2" />
    <rect x="-24" y="-2" width="48" height="4" fill="#111827" opacity="0.25" />
    {/* shorts */}
    <rect x="-22" y="42" width="44" height="26" rx="6" fill="#111827" />
    {/* legs */}
    <rect x="-18" y="66" width="12" height="22" rx="4" fill="#111827" />
    <rect x="6" y="66" width="12" height="22" rx="4" fill="#111827" />
    {/* head */}
    <circle cx="0" cy="-56" r="14" fill="#f5d9bd" stroke="#111" strokeWidth="1.5" />
  </g>
);

const Ball = () => (
  <g className="penalty-ball">
    <circle cx="0" cy="0" r="17" fill="white" stroke="#111" strokeWidth="2.2" />
    <path
      d="M 0 -12 L 10 -4 L 7 8 L -7 8 L -10 -4 Z"
      fill="#111"
      opacity="0.85"
    />
    <circle cx="0" cy="-12" r="2.5" fill="#111" />
    <circle cx="10" cy="-4" r="2.5" fill="#111" />
    <circle cx="-10" cy="-4" r="2.5" fill="#111" />
    <circle cx="7" cy="8" r="2.5" fill="#111" />
    <circle cx="-7" cy="8" r="2.5" fill="#111" />
  </g>
);

const PenaltyShootout = () => {
  useSeo({
    title: 'Penalty shootout - Simulator jedanaesteraca | StatArena',
    description:
      'Besplatna online igra jedanaesteraca. Pucaj i brani protiv kompjutera, klikom u gol uputi udarac i odaberi stranu za obranu. Radi u pregledniku bez instalacije.',
    canonical: 'https://statarena.app/penalty-shootout',
    robots: 'index, follow, max-image-preview:large',
    ogUrl: 'https://statarena.app/penalty-shootout',
    ogTitle: 'Penalty Shootout - Simulator jedanaesteraca',
    ogDescription:
      'Klik za udarac, odaberi stranu za obranu. Klasičnih 5 udaraca + produžeci. Radi odmah u pregledniku.',
  });

  useJsonLd('penalty-software', SOFTWARE_JSONLD);
  useJsonLd('penalty-faq', FAQ_JSONLD);
  useJsonLd('penalty-breadcrumb', BREADCRUMB_JSONLD);

  const svgRef = useRef(null);
  const [phase, setPhase] = useState('intro');
  // intro | player-shoot | player-shot-flying | player-shot-result
  // | player-save | ai-shot-flying | ai-shot-result | game-over
  const [playerShots, setPlayerShots] = useState([]);
  const [aiShots, setAiShots] = useState([]);
  const [ballPos, setBallPos] = useState({ x: PENALTY_SPOT.x, y: PENALTY_SPOT.y });
  const [keeperDive, setKeeperDive] = useState('center');
  const [instantReset, setInstantReset] = useState(true);
  const [banner, setBanner] = useState(null);

  const playerGoals = useMemo(
    () => playerShots.filter((s) => s === 'goal').length,
    [playerShots]
  );
  const aiGoals = useMemo(
    () => aiShots.filter((s) => s === 'goal').length,
    [aiShots]
  );

  const inSuddenDeath =
    playerShots.length >= REGULATION_ROUNDS && aiShots.length >= REGULATION_ROUNDS;
  const playerWin = phase === 'game-over' && playerGoals > aiGoals;
  const aiWin = phase === 'game-over' && aiGoals > playerGoals;
  const isShooting = phase === 'player-shoot';
  const isDefending = phase === 'player-save';
  const showBanner = Boolean(banner);

  // Reset positions when entering interactive phases
  useEffect(() => {
    if (phase === 'player-shoot' || phase === 'player-save') {
      setInstantReset(true);
      setBallPos({ x: PENALTY_SPOT.x, y: PENALTY_SPOT.y });
      setKeeperDive('center');
      setBanner(null);
      const raf1 = requestAnimationFrame(() => {
        const raf2 = requestAnimationFrame(() => setInstantReset(false));
        return () => cancelAnimationFrame(raf2);
      });
      return () => cancelAnimationFrame(raf1);
    }
    return undefined;
  }, [phase]);

  // After a shot result, schedule the next phase
  useEffect(() => {
    if (phase !== 'player-shot-result' && phase !== 'ai-shot-result') return undefined;
    const t = setTimeout(() => {
      const pCount = playerShots.length;
      const aCount = aiShots.length;
      const pG = playerShots.filter((s) => s === 'goal').length;
      const aG = aiShots.filter((s) => s === 'goal').length;

      const pRem = Math.max(0, REGULATION_ROUNDS - pCount);
      const aRem = Math.max(0, REGULATION_ROUNDS - aCount);
      const earlyOver = pG > aG + aRem || aG > pG + pRem;

      const regDone = pCount >= REGULATION_ROUNDS && aCount >= REGULATION_ROUNDS;

      if (earlyOver) {
        setPhase('game-over');
        return;
      }
      if (regDone && pCount === aCount && pG !== aG) {
        setPhase('game-over');
        return;
      }

      if (pCount <= aCount) {
        setPhase('player-shoot');
      } else {
        setPhase('player-save');
      }
    }, 1700);
    return () => clearTimeout(t);
  }, [phase, playerShots, aiShots]);

  const takeShot = useCallback(({ targetX, targetY, shooter, dive }) => {
    const ballZone = getZoneFromX(targetX);
    const saved = computeSave(ballZone, dive, targetY);
    const result = saved ? 'save' : 'goal';

    setInstantReset(false);
    setKeeperDive(dive);
    setBallPos({ x: targetX, y: targetY });
    setPhase(shooter === 'player' ? 'player-shot-flying' : 'ai-shot-flying');

    setTimeout(() => {
      if (shooter === 'player') {
        setPlayerShots((prev) => [...prev, result]);
        setPhase('player-shot-result');
      } else {
        setAiShots((prev) => [...prev, result]);
        setPhase('ai-shot-result');
      }
      setBanner({
        text: result === 'goal' ? 'GOL!' : 'OBRANA!',
        type: result,
      });
    }, 620);
  }, []);

  const handleGoalClick = (e) => {
    if (phase !== 'player-shoot') return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * VB_W;
    const svgY = ((e.clientY - rect.top) / rect.height) * VB_H;

    // Only register click if it's near/in the goal area
    const padX = 40;
    const padY = 60;
    if (
      svgX < GOAL.x - padX ||
      svgX > GOAL.x + GOAL.w + padX ||
      svgY < GOAL.y - padY ||
      svgY > GOAL.y + GOAL.h + padY
    ) {
      return;
    }

    const targetX = clampVal(svgX, GOAL.x + GOAL_INNER_PAD, GOAL.x + GOAL.w - GOAL_INNER_PAD);
    const targetY = clampVal(svgY, GOAL.y + GOAL_INNER_PAD, GOAL.y + GOAL.h - GOAL_INNER_PAD);
    takeShot({ targetX, targetY, shooter: 'player', dive: pickAIDive() });
  };

  const handleDive = (dir) => {
    if (phase !== 'player-save') return;
    const shot = pickAIShot();
    takeShot({ targetX: shot.x, targetY: shot.y, shooter: 'ai', dive: dir });
  };

  const startGame = () => {
    setPlayerShots([]);
    setAiShots([]);
    setBanner(null);
    setInstantReset(true);
    setBallPos({ x: PENALTY_SPOT.x, y: PENALTY_SPOT.y });
    setKeeperDive('center');
    setPhase('player-shoot');
  };

  const keeperPos = KEEPER_POS[keeperDive];
  const turnHint = (() => {
    if (phase === 'intro') return 'Pritisni Start za početak meča';
    if (phase === 'player-shoot')
      return inSuddenDeath
        ? 'Sudden death - pucaš! Promaši i izgubio si.'
        : 'Tvoj udarac - klikni u gol da pucaš';
    if (phase === 'player-save')
      return inSuddenDeath
        ? 'Sudden death - braniš! Obrani ili produžavamo.'
        : 'AI šutira - odaberi stranu za obranu';
    if (phase === 'player-shot-flying' || phase === 'ai-shot-flying') return 'Udarac...';
    if (phase === 'player-shot-result' || phase === 'ai-shot-result')
      return banner?.text || '';
    if (phase === 'game-over') {
      if (playerWin) return 'POBJEDA! 🏆';
      if (aiWin) return 'Poraz - pokušaj ponovno!';
      return 'Izjednačeno';
    }
    return '';
  })();

  return (
    <div className="landing penalty-page">
      <PublicPageNav className="penalty-nav" />

      <main className="penalty-shell">
        <div className="penalty-header">
          <h1>Penalty shootout - simulator jedanaesteraca</h1>
          <p>
            Pucaj i brani jedanaesterce protiv kompjutera. Klik u gol šalje udarac,
            a klikom na gumb odabireš stranu na koju ćeš skočiti u obrani.
          </p>
        </div>

        <div className="penalty-scoreboard">
          <ScoreRow
            label="TI"
            shots={playerShots}
            total={REGULATION_ROUNDS}
            active={isShooting}
            highlight={isShooting ? 'pucaš' : null}
          />
          <div className="penalty-score-big">
            <span className={playerWin ? 'is-win' : ''}>{playerGoals}</span>
            <span className="penalty-score-sep">:</span>
            <span className={aiWin ? 'is-win' : ''}>{aiGoals}</span>
          </div>
          <ScoreRow
            label="AI"
            shots={aiShots}
            total={REGULATION_ROUNDS}
            active={isDefending}
            highlight={isDefending ? 'braniš' : null}
          />
          {inSuddenDeath && phase !== 'game-over' && (
            <div className="penalty-sudden">
              <span role="img" aria-label="Munja">⚡</span> Sudden death - prvi koji pogriješi, izgubio je
            </div>
          )}
        </div>

        <div className={`penalty-stage ${isShooting ? 'is-shooting' : ''}`}>
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            className="penalty-svg"
            onClick={handleGoalClick}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Teren za jedanaesterce"
          >
            <defs>
              <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#0f172a" />
                <stop offset="1" stopColor="#1e293b" />
              </linearGradient>
              <linearGradient id="grass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#2d7a3b" />
                <stop offset="1" stopColor="#0f3a18" />
              </linearGradient>
              <pattern
                id="netPattern"
                x="0"
                y="0"
                width="28"
                height="28"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 28 0 L 0 0 0 28"
                  fill="none"
                  stroke="rgba(255,255,255,0.35)"
                  strokeWidth="1.2"
                />
                <path
                  d="M 0 0 L 28 28 M 28 0 L 0 28"
                  fill="none"
                  stroke="rgba(255,255,255,0.18)"
                  strokeWidth="0.8"
                />
              </pattern>
              <radialGradient id="spotlight" cx="0.5" cy="0.3" r="0.7">
                <stop offset="0" stopColor="rgba(255,255,255,0.08)" />
                <stop offset="1" stopColor="rgba(255,255,255,0)" />
              </radialGradient>
            </defs>

            {/* sky / backdrop behind goal */}
            <rect x="0" y="0" width={VB_W} height={GOAL.y + GOAL.h / 2} fill="url(#sky)" />
            <rect x="0" y="0" width={VB_W} height={VB_H} fill="url(#spotlight)" />

            {/* grass */}
            <rect x="0" y={GOAL.y + GOAL.h / 2} width={VB_W} height={VB_H} fill="url(#grass)" />

            {/* grass stripes */}
            {[0, 1, 2, 3].map((i) => (
              <rect
                key={i}
                x="0"
                y={GOAL.y + GOAL.h / 2 + i * 80}
                width={VB_W}
                height="40"
                fill="rgba(255,255,255,0.04)"
              />
            ))}

            {/* penalty area lines */}
            <rect
              x="60"
              y={GOAL.y + GOAL.h - 10}
              width={VB_W - 120}
              height={VB_H - (GOAL.y + GOAL.h - 10)}
              fill="none"
              stroke="rgba(255,255,255,0.55)"
              strokeWidth="4"
            />
            <rect
              x="200"
              y={GOAL.y + GOAL.h + 70}
              width={VB_W - 400}
              height={VB_H - (GOAL.y + GOAL.h + 70) - 20}
              fill="none"
              stroke="rgba(255,255,255,0.45)"
              strokeWidth="3.5"
            />

            {/* penalty spot */}
            <circle cx={PENALTY_SPOT.x} cy={PENALTY_SPOT.y} r="7" fill="white" />

            {/* net (inside goal) */}
            <rect
              x={GOAL.x + 4}
              y={GOAL.y + 4}
              width={GOAL.w - 8}
              height={GOAL.h - 8}
              fill="url(#netPattern)"
              opacity="0.95"
            />

            {/* shooting zones hint */}
            {isShooting && (
              <g className="penalty-zones" pointerEvents="none">
                <line
                  x1={GOAL.x + GOAL.w / 3}
                  y1={GOAL.y + 6}
                  x2={GOAL.x + GOAL.w / 3}
                  y2={GOAL.y + GOAL.h - 6}
                  stroke="rgba(250,204,21,0.35)"
                  strokeWidth="2"
                  strokeDasharray="6 6"
                />
                <line
                  x1={GOAL.x + (2 * GOAL.w) / 3}
                  y1={GOAL.y + 6}
                  x2={GOAL.x + (2 * GOAL.w) / 3}
                  y2={GOAL.y + GOAL.h - 6}
                  stroke="rgba(250,204,21,0.35)"
                  strokeWidth="2"
                  strokeDasharray="6 6"
                />
              </g>
            )}

            {/* goal frame */}
            <rect
              x={GOAL.x}
              y={GOAL.y}
              width={GOAL.w}
              height={GOAL.h}
              fill="none"
              stroke="white"
              strokeWidth="10"
              strokeLinejoin="round"
            />

            {/* keeper */}
            <g
              style={{
                transform: `translate(${keeperPos.x}px, ${keeperPos.y - 45}px) rotate(${keeperPos.angle}deg)`,
                transformOrigin: '0 0',
                transition: instantReset
                  ? 'none'
                  : 'transform 0.42s cubic-bezier(0.2, 0.8, 0.25, 1)',
              }}
            >
              <KeeperShape />
            </g>

            {/* ball */}
            <g
              style={{
                transform: `translate(${ballPos.x}px, ${ballPos.y}px)`,
                transformOrigin: '0 0',
                transition: instantReset
                  ? 'none'
                  : 'transform 0.62s cubic-bezier(0.25, 0.85, 0.3, 1)',
              }}
            >
              <Ball />
            </g>
          </svg>

          {showBanner && (
            <div className={`penalty-banner penalty-banner-${banner.type}`} aria-live="polite">
              {banner.text}
            </div>
          )}

          {phase === 'intro' && (
            <div className="penalty-overlay">
              <div className="penalty-overlay-card">
                <h2>Spreman za jedanaesterce?</h2>
                <p>
                  Odigraj {REGULATION_ROUNDS} udaraca i {REGULATION_ROUNDS} obrana protiv
                  kompjutera. Ako završi izjednačeno - ide sudden death.
                </p>
                <button className="btn btn-primary btn-lg" onClick={startGame}>
                  Start meč
                </button>
              </div>
            </div>
          )}

          {phase === 'game-over' && (
            <div className="penalty-overlay">
              <div className="penalty-overlay-card">
                <h2 className={playerWin ? 'is-win' : aiWin ? 'is-lose' : ''}>
                  {playerWin ? '🏆 Pobjeda!' : aiWin ? '😔 Poraz' : 'Izjednačeno'}
                </h2>
                <p className="penalty-final-score">
                  Konačni rezultat: <strong>{playerGoals} : {aiGoals}</strong>
                </p>
                <button className="btn btn-primary btn-lg" onClick={startGame}>
                  Igraj ponovno
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="penalty-controls">
          <div className="penalty-turnhint" role="status" aria-live="polite">
            {turnHint}
          </div>

          {phase === 'player-save' && (
            <div className="penalty-dive-buttons">
              <button
                type="button"
                className="penalty-dive-btn"
                onClick={() => handleDive('left')}
                aria-label="Skoči lijevo"
              >
                <span className="penalty-dive-arrow">←</span>
                <span>Lijevo</span>
              </button>
              <button
                type="button"
                className="penalty-dive-btn penalty-dive-btn-center"
                onClick={() => handleDive('center')}
                aria-label="Ostani u sredini"
              >
                <span className="penalty-dive-arrow">●</span>
                <span>Sredina</span>
              </button>
              <button
                type="button"
                className="penalty-dive-btn"
                onClick={() => handleDive('right')}
                aria-label="Skoči desno"
              >
                <span className="penalty-dive-arrow">→</span>
                <span>Desno</span>
              </button>
            </div>
          )}

          {phase !== 'intro' && phase !== 'game-over' && (
            <button
              type="button"
              className="penalty-reset-btn"
              onClick={startGame}
              title="Pokreni novu igru"
            >
              ↻ Novi meč
            </button>
          )}
        </div>
      </main>

      <section className="landing-section penalty-info">
        <div className="section-header landing-section-header">
          <h2>Kako radi Penalty Shootout?</h2>
          <p className="landing-section-sub">
            Brz, besplatan i jednostavan online simulator jedanaesteraca. Klik za udarac,
            gumb za obranu - bez registracije i bez instalacije.
          </p>
        </div>

        <div className="landing-steps">
          <div className="landing-step">
            <div className="landing-step-num">1</div>
            <h3>Pucaj</h3>
            <p>Klikni u gol tamo gdje želiš pogoditi. Što bliže uglu, to veća šansa za gol.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-num">2</div>
            <h3>Brani</h3>
            <p>Kad AI šutira, odaberi stranu (lijevo, sredina, desno) prije udarca.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-num">3</div>
            <h3>5 + 5 udaraca</h3>
            <p>Naizmjence tvoji i AI udarci. Vodi tko ima više golova na kraju.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-num">4</div>
            <h3>Sudden death</h3>
            <p>Ako je izjednačeno, igra se dok netko ne pogriješi.</p>
          </div>
        </div>
      </section>

      <section className="landing-section landing-section-alt">
        <div className="section-header landing-section-header">
          <h2>Često postavljena pitanja o Penalty Shootoutu</h2>
        </div>
        <div className="faq-list">
          {FAQ_ITEMS.map((item) => (
            <FaqItem key={item.question} question={item.question}>
              <p>{item.answer}</p>
            </FaqItem>
          ))}
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <span className="landing-logo-mark" role="img" aria-label="Nogometna lopta">⚽</span>
            <span className="landing-logo-text">StatArena</span>
            <span className="landing-footer-tag">Aplikacija za rekreativni nogomet</span>
          </div>
          <div className="landing-footer-links">
            <Link to="/">Početna</Link>
            <Link to="/generator-timova">Generator timova</Link>
            <Link to="/raspored-turnira">Raspored turnira</Link>
            <Link to="/semafor-online">Online semafor</Link>
            <Link to="/penalty-shootout">Penalty shootout</Link>
            <Link to="/login">Prijava</Link>
            <Link to="/register">Registracija</Link>
          </div>
          <div className="landing-footer-copy">
            © {new Date().getFullYear()} StatArena · statarena.app
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PenaltyShootout;

